import { THEME_COLOR } from "@utils/constants";
import classNames from "classnames";
import Link from "next/link";
import React, { useEffect } from "react";
import { FlexContainer } from "@components/ui/flex_container";
import ReactLoading from "react-loading";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSort,
    faSortAlphaAsc,
    faSortAlphaDesc,
    faSortAsc,
    faSortDesc,
    faSortNumericAsc,
    faSortNumericDesc,
} from "@fortawesome/free-solid-svg-icons";
import InfoSectionErrorText from "src/InfoSectionErrorText/InfoSectionErrorText";
import styles from "./MittTable.module.scss";

export default function MittTable({
    header,
    footer,
    data,
    selectable,
    getRowData,
    getRowLink,
    getRowOnClick,
    getRowStyle,
    getHeaderStyle,
    renderRow,
    errorText,
    errorSubText,
    isLoading,
    style,
    compact,
    className,
}) {
    const [tableData, setTableData] = React.useState(Array.isArray(data) ? data : []);
    const [sort, setSort] = React.useState({ columnIndex: null, direction: null });
    useEffect(() => {
        setTableData(sortData(Array.isArray(data) ? data : []));
    }, [data]);

    useEffect(() => {
        setTableData(sortData(tableData));
    }, [sort]);

    const sortData = (data = []) => {
        if (!data || !Array.isArray(data)) return [];
        if (sort?.columnIndex !== null) {
            const sortIndex = sort?.columnIndex;
            const sortDirection = sort?.direction;
            let sortedData = [];
            if (header?.[sortIndex]?.sort) {
                sortedData = [...data].sort((a, b) => header[sortIndex]?.sort(a, b, sortDirection));
            } else if (header?.[sortIndex]?.sortString) {
                sortedData = [...data].sort((a, b) => {
                    const aData = getRowData?.(a)?.[sortIndex];
                    const bData = getRowData?.(b)?.[sortIndex];
                    if (aData === bData) return 0;
                    if (aData === undefined || aData === null) return 1;
                    if (bData === undefined || bData === null) return -1;

                    // Get the string to sort by using the sortString function
                    const aSortString = header[sortIndex].sortString(aData);
                    const bSortString = header[sortIndex].sortString(bData);

                    const comparison = aSortString?.localeCompare(bSortString);
                    if (comparison === -1) return sortDirection === "asc" ? -1 : 1;
                    if (comparison === 1) return sortDirection === "asc" ? 1 : -1;
                    return 0;
                });
            } else {
                sortedData = [...data].sort((a, b) => {
                    const aData = getRowData?.(a)?.[sortIndex];
                    const bData = getRowData?.(b)?.[sortIndex];
                    if (aData === bData) return 0;
                    if (aData === undefined || aData === null) return 1;
                    if (bData === undefined || bData === null) return -1;
                    const comparison = aData?.toString()?.localeCompare(bData?.toString());
                    if (comparison === -1) return sortDirection === "asc" ? -1 : 1;
                    if (comparison === 1) return sortDirection === "asc" ? 1 : -1;
                    return 0;
                });
            }
            return sortedData;
        }
        return data;
    };

    return (
        <div className={className} style={{ overflowX: "auto", ...style }}>
            <table
                className={classNames(styles.table, {
                    [styles.selectable]: selectable,
                    [styles.compact]: compact,
                })}
            >
                <thead>
                    <tr>
                        {header?.map?.((headerColumn, index) => {
                            if (!headerColumn) return null;
                            let sortIcon = null;

                            // Check if this column is being sorted
                            const isSorted = index === sort?.columnIndex;

                            if (isSorted) {
                                if (headerColumn?.dataType === "number") {
                                    sortIcon =
                                        sort?.direction === "asc"
                                            ? faSortNumericAsc
                                            : faSortNumericDesc;
                                } else if (headerColumn?.dataType === "text") {
                                    sortIcon =
                                        sort?.direction === "asc"
                                            ? faSortAlphaAsc
                                            : faSortAlphaDesc;
                                } else {
                                    sortIcon = sort?.direction === "asc" ? faSortAsc : faSortDesc;
                                }
                            } else if (headerColumn?.dataType === "number") {
                                sortIcon = faSortNumericAsc;
                            } else if (headerColumn?.dataType === "text") {
                                sortIcon = faSortAlphaAsc;
                            } else {
                                sortIcon = faSort;
                            }

                            return (
                                <th
                                    key={headerColumn}
                                    style={{
                                        cursor: headerColumn?.orderable && "pointer",
                                        whiteSpace: "nowrap",
                                        width: headerColumn?.width ? headerColumn?.width : "auto",
                                    }}
                                    onClick={() => {
                                        return (
                                            headerColumn?.orderable &&
                                            setSort({
                                                columnIndex: index,
                                                direction:
                                                    sort?.columnIndex === index &&
                                                    sort?.direction === "asc"
                                                        ? "desc"
                                                        : "asc",
                                            })
                                        );
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 8,
                                            ...getHeaderStyle?.(headerColumn, index),
                                        }}
                                    >
                                        {typeof headerColumn === "object"
                                            ? headerColumn?.title
                                            : headerColumn}

                                        {headerColumn?.orderable && (
                                            <FontAwesomeIcon
                                                icon={sortIcon}
                                                style={{
                                                    color: isSorted ? "#777" : "#ddd",
                                                }}
                                            />
                                        )}
                                    </div>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {tableData?.map((rowData, index) => {
                        if (!rowData) return null;
                        const rowKey = rowData.id || `row-${index}`;
                        if (renderRow === undefined) {
                            return (
                                <MittTableRow
                                    key={rowKey}
                                    link={getRowLink?.(rowData)}
                                    rowData={getRowData?.(rowData)}
                                    onClick={getRowOnClick?.(rowData)}
                                />
                            );
                        } else {
                            const rowStyle = {
                                cursor: getRowOnClick?.(rowData) && "pointer",
                                ...getRowStyle?.(rowData, index),
                            };
                            if (getRowLink?.(rowData)) {
                                return (
                                    <Link
                                        key={rowKey}
                                        href={getRowLink?.(rowData)}
                                        onClick={getRowOnClick?.(rowData)}
                                    >
                                        <tr style={rowStyle}>{renderRow?.(rowData, index)}</tr>
                                    </Link>
                                );
                            }
                            return (
                                <tr
                                    key={rowKey}
                                    style={rowStyle}
                                    onClick={getRowOnClick?.(rowData)}
                                >
                                    {renderRow?.(rowData, index)}
                                </tr>
                            );
                        }
                    })}
                </tbody>
                {footer && (
                    <tfoot>
                        <tr>
                            {footer.map((footerColumn) => {
                                return <td key={footerColumn}>{footerColumn}</td>;
                            })}
                        </tr>
                    </tfoot>
                )}
            </table>
            {(isLoading !== undefined ? isLoading : tableData === undefined && !errorText) && (
                <FlexContainer justifyContent="center">
                    <ReactLoading type={"bubbles"} color={THEME_COLOR} width="90px" height="90px" />
                </FlexContainer>
            )}
            <InfoSectionErrorText text={errorText} subtext={errorSubText} />
        </div>
    );
}

function MittTableRow({ link, rowData, onClick = () => {} }) {
    const row = () => {
        return (
            <tr onClick={onClick}>
                {rowData?.map((columnData, colIndex) => {
                    // Use index as key since columnData might be a React element
                    return <td key={colIndex}>{columnData}</td>;
                })}
            </tr>
        );
    };

    if (!link) {
        return row();
    }

    return <Link href={link}>{row()}</Link>;
}
